import React from "react";
import { TextField, Button, Typography, useMediaQuery } from "@mui/material";
import Grid from '@mui/material/Grid2';
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useAppStore } from "../../stores";

import RoleDropdownHOCForUserManagementAccountCreation from "components/RoleDropdownHOCForUserManagementAccountCreation/RoleDropdownHOCForUserManagementAccountCreation";
import GenericSpinner from "components/GenericSpinner/GenericSpinner";
import _ from "lodash";
import { useSnackbar } from "notistack";
import RecordConflictDialog from "components/RecordConflictDialog/RecordConflictDialog";

interface formStateType {
  affPinNum: string;
  memberId: string;
}

interface SelectedUserType {
  role: { roleName: string; roleId: number };
  user?: {
    userId: number;
    firstName: string;
    lastName: string;
    username: string;
  };
  memberId?: string;
  affPinNum?: string;
  [key: string]: unknown;
}

interface IUserAccountCreationProps {
  forUserManagement?: boolean;
  selectedUser?: SelectedUserType;
  dropdownValue?: string | number;
  dropDownOnChange?: (e: { target: { value: unknown } }) => void;
  closeParentModal?: (value: boolean) => void;
  reloadUsers?: () => void;
  formState?: formStateType;
  handleFormChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedPropsValue?: string | number;
  setSelectedPropsValue?: (value: string | number) => void;
  setSelectedPropsText?: (text: string) => void;
  memberIdRequired?: (value: number, text?: string) => boolean;
  affiliatePinRequired?: (value: number) => boolean;
  toggleForm?: () => void;
  isLogin?: boolean;
  conventionLoadPending?: boolean;
  registrationClosed?: boolean;
  onSwitchToLogin?: () => void;
  isLandingPage?: boolean;
}

const UserAccountCreation: React.FC<IUserAccountCreationProps> = (props) => {
  const displayMobile = useMediaQuery("(max-width:959px)");

  const { enqueueSnackbar } = useSnackbar();

  const setUserCreateDetails = useAppStore((state) => state.setUserCreateDetails);

  const navigate = useNavigate();

  const isForUserManagement = props.forUserManagement;

  const dropdownOnChange = (e: { target: { value: unknown } }) => {
    props.setSelectedPropsValue?.(String(e.target.value));
    
    // Also call the parent's dropDownOnChange if it exists
    if (props.dropDownOnChange) {
      props.dropDownOnChange(e);
    }

    const selectedText = String(e.target.value) || '';
    if (props.selectedUser) {
      props.selectedUser.role.roleName = selectedText;
    }
    props.setSelectedPropsText?.(selectedText);
  };

  const [
    accountCreationUserManagementRequestPending,
    setAccountCreationUserManagementRequestPending,
  ] = React.useState(false);

  const [payload, setPayload] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    retypePassword: "",
  });

  const [conflictDialogState, setConflictDialogState] = React.useState({
    records: [],
    open: false,
    selectedRecord: null,
  });

  const handleClose = (value: any) => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
      selectedRecord: value,
    });
  };

  const handleListItemClick = (record: any) => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
      selectedRecord: record,
    });

    const { retypePassword, ...payloadToSend } = payload;
    submitAccountCreationRequest({
      ...payloadToSend,
      RoleId: props.selectedPropsValue,
      affPinNum: props.formState ? props.formState.affPinNum : "",
      memberId: props.formState ? props.formState.memberId : "",
      selectedRecord: record,
    });
  };

  const submitAccountCreationRequest = (payload: object) => {
    setAccountCreationUserManagementRequestPending(true);
    axios
      .post("/api/Users/createUserAccountAdmin", payload)
      .then((response) => {
        if (response) {
          if (props.closeParentModal) {
            props.closeParentModal(false);
          }
          enqueueSnackbar("Account Created!", {
            variant: "success",
          });
          if (props.reloadUsers) {
            props.reloadUsers();
          }
        }
        setAccountCreationUserManagementRequestPending(false);
      })
      .catch(({ response }) => {
        setAccountCreationUserManagementRequestPending(false);
        console.log("submitAccountCreationRequest response" + response)
        if (_.get(response, "data.error.individualRecordConflict")) {
          setConflictDialogState({
            ...conflictDialogState,
            records: _.get(response, "data.recordListing"),
            selectedRecord: null,
            open: true,
          });
        } else {
          enqueueSnackbar(
            "Account creation error: " +
              (_.get(response, "data.error.message") || "Unspecified error"),
            {
              variant: "error",
            }
          );
        }
      });
  };

  const submitRegistrationForm = (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    if (e.target && payload.password === payload.retypePassword) {
      if (isForUserManagement) {
        const { roleDropdown } = e.target as HTMLFormElement;
        const { retypePassword, ...payloadToSend } = payload;
        submitAccountCreationRequest({
          ...payloadToSend,
          MemberId: props.formState?.memberId ?? "",
          AffPinNum: props.formState?.affPinNum ?? "",
          RoleId: Number(roleDropdown.value),
          IsAffiliateStaff: Number(roleDropdown.value) === 7,
          SkipLoginStep: true,
        });
      } else {
        setUserCreateDetails(payload);
        navigate("/on-boarding");
      }
    }
  };

  const [errors, setErrors] = React.useState({
    firstName: { error: false, msg: "" },
    lastName: { error: false, msg: "" },
    email: { error: false, msg: "" },
    memberId: { error: false, msg: "" },
    affPinNum: { error: false, msg: "" },
    password: { error: false, msg: "" },
    retypePassword: { error: false, msg: "" },
  });

  const [newPassword, setNewPassword] = React.useState("");
  const [retypeNewPassword, setRetypeNewPassword] = React.useState("");

  React.useEffect(() => {
    if (newPassword !== "" || retypeNewPassword !== "") {
      let newPasswordError = false;
      let newPasswordMsg = "";
      let retypePasswordError = false;
      let retypePasswordMsg = "";

      if (
        !/(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/.test(
          newPassword
        ) ||
        newPassword.length === 0
      ) {
        newPasswordError = true;
        newPasswordMsg =
          "Password must contain one uppercase and one lowercase character, one number/special character, and at least 8 characters";
      } else {
        newPasswordError = false;
        newPasswordMsg = "";
      }

      if (retypeNewPassword.length > 0 && newPassword !== retypeNewPassword) {
        retypePasswordError = true;
        retypePasswordMsg = "Passwords must match.";
      } else {
        retypePasswordError = false;
        retypePasswordMsg = "";
      }

      setErrors({
        ...errors,
        password: { error: newPasswordError, msg: newPasswordMsg },
        retypePassword: { error: retypePasswordError, msg: retypePasswordMsg },
      });
    }
  }, [newPassword, retypeNewPassword]);

  const passwordInputOnChange = (e: React.SyntheticEvent<EventTarget>) => {
    const { name, value } = e.target as HTMLFormElement;
    if (name === "password") {
      setNewPassword(value);
    } else {
      setRetypeNewPassword(value);
    }
    handleUserInput(e);
  };

  const handleUserInput = (e: React.SyntheticEvent<EventTarget>) => {
    const { name, value } = e.target as HTMLFormElement;
    let isError = false;
    let errorMsg = "";
    switch (name) {
      case "email":
        if (!value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/)) {
          isError = true;
          errorMsg = "Please enter a valid email address";
        }
        break;
      default:
        break;
    }
    setPayload({ ...payload, [name]: value });
    setErrors({
      ...errors,
      [name]: { error: isError, msg: errorMsg },
    });
  };

  const handleSwitchToLogin = () => {
    if (props.onSwitchToLogin) {
      props.onSwitchToLogin();
    } else if (props.toggleForm) {
      props.toggleForm();
    }
  };

  // New landing page design
  if (props.isLandingPage) {
    if (props.conventionLoadPending) {
      return (
        <div className="registration-container-new">
          <GenericSpinner padding={0} includeOverlay={false} />
        </div>
      );
    }

    if (props.registrationClosed) {
      return (
        <div className="registration-container-new">
          <div className="registration-header-section">
            <h2 className="registration-title">Registration Closed</h2>
          </div>
          <Typography>
            Convention Registration is currently closed. If you need assistance, please contact aftconvention@aft.org.
          </Typography>
        </div>
      );
    }

    return (
      <div className="registration-container-new">
        <div className="registration-header-section">
          <h2 className="registration-title">Register today</h2>
          <p className="registration-subtitle">All fields required</p>
          <p style={{ color: 'red', textAlign: 'center', fontWeight: 'bold', margin: '8px 0 0' }}>
            Everyone must create a new account.<br />
            Logins for previous conventions will not work.
          </p>
        </div>

        <form method="post" onSubmit={submitRegistrationForm} className="registration-form-new">
          <div className="registration-field">
            <TextField
              id="firstname-input"
              placeholder="First Name"
              autoComplete="off"
              fullWidth
              required
              type="text"
              name="firstName"
              variant="outlined"
              onChange={handleUserInput}
              helperText={errors.firstName.error ? errors.firstName.msg : ""}
              error={errors.firstName.error}
            />
          </div>

          <div className="registration-field">
            <TextField
              id="lastname-input"
              placeholder="Last Name"
              autoComplete="off"
              type="text"
              required
              name="lastName"
              fullWidth
              variant="outlined"
              onChange={handleUserInput}
              helperText={errors.lastName.error ? errors.lastName.msg : ""}
              error={errors.lastName.error}
            />
          </div>

          <div className="registration-field">
            <TextField
              id="email-register-input"
              placeholder="Email"
              type="text"
              autoComplete="off"
              required
              name="email"
              fullWidth
              variant="outlined"
              onChange={handleUserInput}
              helperText={errors.email.error ? errors.email.msg : ""}
              error={errors.email.error}
            />
          </div>

          <div className="registration-field">
            <TextField
              id="register-password-input"
              placeholder="Password"
              type="password"
              name="password"
              required
              autoComplete="off"
              fullWidth
              variant="outlined"
              onChange={passwordInputOnChange}
              helperText={errors.password.error ? errors.password.msg : ""}
              error={errors.password.error}
            />
          </div>
          
          <div className="registration-field">
            <TextField
              id="register-password-retype-input"
              placeholder="Retype Password"
              type="password"
              name="retypePassword"
              autoComplete="off"
              required
              fullWidth
              variant="outlined"
              onChange={passwordInputOnChange}
              helperText={errors.retypePassword.error ? errors.retypePassword.msg : ""}
              error={errors.retypePassword.error}
            />
          </div>

          <div className="registration-button-wrapper" style={{ width: '100%', padding: '0', margin: '24px 0' }}>
            <Button
              className="registration-submit-btn"
              type="submit"
              size="large"
              variant="contained"
              color="primary"
              style={{ width: '100%' }}
              disabled={
                errors.email.error ||
                errors.password.error ||
                errors.retypePassword.error
              }
            >
              <Typography>Create Account</Typography>
            </Button>
          </div>
        </form>

        <div className="registration-login-prompt">
          <span>Already have an account?</span>
          <a
            href="#"
            className="registration-login-link"
            onClick={(e) => {
              e.preventDefault();
              handleSwitchToLogin();
            }}
          >
            Login here
          </a>
        </div>

        <RecordConflictDialog
          handleClose={handleClose}
          handleListItemClick={handleListItemClick}
          open={conflictDialogState.open}
          records={conflictDialogState.records}
        />
      </div>
    );
  }

  // Original design for user management and other contexts
  const renderTitleCorrectly = () => {
    if (displayMobile || props.forUserManagement) {
      return null;
    }
    return (
      <div className="form-title">
        <h2>Register Today</h2>
        <span>
          <small>All Fields Required</small>
        </span>
      </div>
    );
  };

  const renderBody = () => {
    if (props.conventionLoadPending) {
      return (
        <Grid container alignContent="center">
          <Grid size={{ md: 12 }}>
            <GenericSpinner padding={0} includeOverlay={false} />
          </Grid>
        </Grid>
      );
    } else if (props.registrationClosed) {
      return (
        <Grid container alignContent="center">
          <Grid size={{ md: 12 }}>
            <Typography>
              Convention Registration is currently closed. If you need assistance, please contact aftconvention@aft.org.
            </Typography>
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid size={{ xs: 12 }}>
          {renderTitleCorrectly()}
          <Grid container size={{ xs: 12 }}>
            <form
              method="post"
              onSubmit={submitRegistrationForm}
              className="register-today-form w-100"
            >
              {isForUserManagement && (
                <Grid container size={{ xs: 12 }} spacing={3}>
                  <Grid
                    size={{ xs: 12 }}
                    style={{
                      fontSize: "1.25rem",
                      color: "gray",
                    }}
                  >
                    <span>Role Selection:</span>
                  </Grid>
                  <Grid size={{ xs: 12 }} className="pt-2">
                    <RoleDropdownHOCForUserManagementAccountCreation
                      selectedUser={props.selectedUser}
                      onChangeInputFields={props.handleFormChange}
                      selectedPropsValue={props.selectedPropsValue}
                      dropdownOnChange={dropdownOnChange}
                      memberIdRequired={props.memberIdRequired}
                      affiliatePinRequired={props.affiliatePinRequired}
                    />
                  </Grid>
                  {props.affiliatePinRequired?.(Number(props.dropdownValue)) && (
                    <Grid size={{ xs: 12, md: 6 }} className="pt-2">
                      <TextField
                        label="Affiliate PIN Number"
                        fullWidth
                        required={true}
                        type="text"
                        onChange={props.handleFormChange}
                        name="affPinNum"
                        variant="outlined"
                        defaultValue={props.selectedUser?.affPinNum}
                      />
                    </Grid>
                  )}
                  <Grid
                    size={{ xs: 12, md: 12 }}
                    style={{
                      fontSize: "1.25rem",
                      color: "gray",
                    }}
                    className="pt-2"
                  >
                    <span>User Information:</span>
                  </Grid>
                </Grid>
              )}

              {props.isLogin && (
                <div style={{ paddingTop: "40px", textAlign: "center" }}>
                  <div className="input-wrapper i-modern">
                    <TextField
                      id="firstname-input"
                      label="First Name"
                      autoComplete="off"
                      fullWidth
                      required
                      type="text"
                      name="firstName"
                      variant="outlined"
                      onChange={handleUserInput}
                      helperText={errors.firstName.error ? errors.firstName.msg : ""}
                      error={errors.firstName.error}
                    />
                  </div>

                  <div className="input-wrapper i-modern">
                    <TextField
                      id="lastname-input"
                      label="Last Name"
                      autoComplete="off"
                      type="text"
                      required
                      name="lastName"
                      fullWidth
                      variant="outlined"
                      onChange={handleUserInput}
                      helperText={errors.lastName.error ? errors.lastName.msg : ""}
                      error={errors.lastName.error}
                    />
                  </div>
                  <div className="input-wrapper i-modern">
                    <TextField
                      id="email-resgister-input"
                      label="Email"
                      type="text"
                      autoComplete="off"
                      required
                      name="email"
                      fullWidth
                      variant="outlined"
                      onChange={handleUserInput}
                      helperText={errors.email.error ? errors.email.msg : ""}
                      error={errors.email.error}
                    />
                  </div>
                  <div className="input-wrapper i-modern">
                    <TextField
                      id="resgister-password-input"
                      label="Password"
                      type="password"
                      name="password"
                      required
                      autoComplete="off"
                      fullWidth
                      variant="outlined"
                      onChange={passwordInputOnChange}
                      helperText={errors.password.error ? errors.password.msg : ""}
                      error={errors.password.error}
                    />
                  </div>
                  <div className="input-wrapper i-modern">
                    <TextField
                      id="resgister-password-retype-input"
                      label="Retype Password"
                      type="password"
                      name="retypePassword"
                      autoComplete="off"
                      required
                      fullWidth
                      variant="outlined"
                      onChange={passwordInputOnChange}
                      helperText={errors.retypePassword.error ? errors.retypePassword.msg : ""}
                      error={errors.retypePassword.error}
                    />
                  </div>
                  <div className="input-wrapper i-modern">
                    <Button
                      className="btn"
                      type="submit"
                      size="large"
                      fullWidth={isForUserManagement ? false : true}
                      variant="contained"
                      color="primary"
                      disabled={
                        errors.email.error ||
                        errors.password.error ||
                        errors.retypePassword.error ||
                        (isForUserManagement && !Number(props.dropdownValue))
                      }
                    >
                      <span style={{ padding: ".55em" }}>Create Account</span>
                    </Button>
                  </div>
                </div>
              )}

              {!props.isLogin && (
                <div>
                  <Grid container className="pt-2" spacing={3} style={{ height: "100%" }}>
                    <Grid size={{ xs: 12, md: isForUserManagement ? 6 : 12 }}>
                      <TextField
                        id="firstname-input"
                        label="First Name"
                        autoComplete="off"
                        fullWidth
                        required
                        type="text"
                        name="firstName"
                        variant="outlined"
                        onChange={handleUserInput}
                        helperText={errors.firstName.error ? errors.firstName.msg : ""}
                        error={errors.firstName.error}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: isForUserManagement ? 6 : 12 }}>
                      <TextField
                        id="lastname-input"
                        label="Last Name"
                        autoComplete="off"
                        type="text"
                        required
                        name="lastName"
                        fullWidth
                        variant="outlined"
                        onChange={handleUserInput}
                        helperText={errors.lastName.error ? errors.lastName.msg : ""}
                        error={errors.lastName.error}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        id="email-resgister-input"
                        label="Email"
                        type="text"
                        autoComplete="off"
                        required
                        name="email"
                        fullWidth
                        variant="outlined"
                        onChange={handleUserInput}
                        helperText={errors.email.error ? errors.email.msg : ""}
                        error={errors.email.error}
                      />
                    </Grid>
                    {props.memberIdRequired?.(props.dropdownValue as number) && (
                      <Grid size={{ xs: 12, md: 12 }}>
                        <TextField
                          id="register-member-id-input"
                          label="Member Id"
                          type="text"
                          name="memberId"
                          autoComplete="off"
                          fullWidth
                          variant="outlined"
                          onChange={props.handleFormChange || handleUserInput}
                          helperText={errors.memberId.error ? errors.memberId.msg : ""}
                          error={errors.memberId.error}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        id="resgister-password-input"
                        label="Password"
                        type="password"
                        name="password"
                        required
                        autoComplete="off"
                        fullWidth
                        variant="outlined"
                        onChange={passwordInputOnChange}
                        helperText={errors.password.error ? errors.password.msg : ""}
                        error={errors.password.error}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        id="resgister-password-retype-input"
                        label="Retype Password"
                        type="password"
                        name="retypePassword"
                        autoComplete="off"
                        required
                        fullWidth
                        variant="outlined"
                        onChange={passwordInputOnChange}
                        helperText={errors.retypePassword.error ? errors.retypePassword.msg : ""}
                        error={errors.retypePassword.error}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }} className="text-center">
                      <Button
                        className="btn"
                        type="submit"
                        size="large"
                        fullWidth={isForUserManagement ? false : true}
                        variant="contained"
                        color="primary"
                        disabled={
                          errors.email.error ||
                          errors.password.error ||
                          errors.retypePassword.error ||
                          (isForUserManagement && !Number(props.dropdownValue))
                        }
                      >
                        <span style={{ padding: ".55em" }}>Create Account</span>
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}
            </form>
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <Grid
      container
      className={`${props.forUserManagement ? "p-0" : "register-today-wrapper"}`}
    >
      {accountCreationUserManagementRequestPending && (
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 12 }}>
            <GenericSpinner includeOverlay={true} padding={25} />
          </Grid>
        </Grid>
      )}
      {renderBody()}
      <RecordConflictDialog
        handleClose={handleClose}
        handleListItemClick={handleListItemClick}
        open={conflictDialogState.open}
        records={conflictDialogState.records}
      />
    </Grid>
  );
};

export default UserAccountCreation;
